/** Schedule management page (FR-SCH-01 through FR-SCH-05). */

import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Button, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, CircularProgress, Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSchedules, useCreateSchedule, useUpdateSchedule, useDeleteSchedule } from "@/hooks/useSchedules";
import { usePlugins } from "@/hooks/usePlugins";
import { usePluginParameters } from "@/hooks/usePluginParameters";
import { getUserManager } from "@/hooks/useAuth";
import ScheduleParameterForm from "@/components/schedules/ScheduleParameterForm";
import { validateScheduleParams } from "@/utils/validateScheduleParams";
import { serializeParams, deserializeParams } from "@/utils/scheduleParamSerialization";
import { reconcileParameters } from "@/utils/reconcileParameters";
import type { Schedule } from "@/types";
import type { ScheduleParameterValues } from "@/types/schedule";

const PRESETS = [
  { label: "Every Monday 8am", value: "0 8 * * 1" },
  { label: "Daily 6am", value: "0 6 * * *" },
  { label: "First of month", value: "0 8 1 * *" },
  { label: "Every hour", value: "0 * * * *" },
];

/** Empty form state for creating a new schedule. */
const INITIAL_FORM = { pluginId: "", cronExpression: "", timezone: "Australia/Sydney", name: "" };

export default function Schedules() {
  const { data, isLoading } = useSchedules();
  const { data: pluginsData } = usePlugins();
  const createSchedule = useCreateSchedule();
  const updateSchedule = useUpdateSchedule();
  const deleteSchedule = useDeleteSchedule();

  // Dialog state: null = closed, "create" = new schedule, schedule object = editing
  const [dialogMode, setDialogMode] = useState<null | "create" | Schedule>(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [paramValues, setParamValues] = useState<ScheduleParameterValues>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  // Determine the pluginId for parameter fetching
  const activePluginId = dialogMode === "create" ? form.pluginId : (dialogMode?.pluginId ?? null);
  const { parameters: pluginParams, isLoading: paramsLoading } = usePluginParameters(activePluginId || null);

  // When editing, deserialize and reconcile stored parameters with current plugin definitions
  useEffect(() => {
    if (dialogMode && dialogMode !== "create" && pluginParams.length > 0) {
      const raw = (dialogMode.parameters ?? {}) as Record<string, unknown>;
      const deserialized = deserializeParams(raw);
      const reconciled = reconcileParameters(pluginParams, deserialized);
      setParamValues(reconciled);
    }
  }, [dialogMode, pluginParams]);

  // When creating, reset param values when plugin changes and we get new definitions
  useEffect(() => {
    if (dialogMode === "create" && pluginParams.length > 0) {
      const reconciled = reconcileParameters(pluginParams, {});
      setParamValues(reconciled);
    } else if (dialogMode === "create" && !form.pluginId) {
      setParamValues({});
    }
  }, [dialogMode, pluginParams, form.pluginId]);

  // Validate parameter values
  const paramErrors = useMemo(
    () => pluginParams.length > 0 ? validateScheduleParams(pluginParams, paramValues) : {},
    [pluginParams, paramValues],
  );
  const hasParamErrors = Object.keys(paramErrors).length > 0;

  const isEditing = dialogMode !== null && dialogMode !== "create";
  const dialogOpen = dialogMode !== null;

  const openCreateDialog = () => {
    setForm(INITIAL_FORM);
    setParamValues({});
    setSaveError(null);
    setDialogMode("create");
  };

  const openEditDialog = (schedule: Schedule) => {
    setForm({
      pluginId: schedule.pluginId,
      cronExpression: schedule.cronExpression,
      timezone: schedule.timezone,
      name: schedule.name ?? "",
    });
    setParamValues({});
    setSaveError(null);
    setDialogMode(schedule);
  };

  const closeDialog = () => {
    setDialogMode(null);
    setSaveError(null);
  };

  const handleCreate = async () => {
    const payload: Record<string, unknown> = { ...form };
    if (form.name) {
      payload.name = form.name;
    } else {
      delete payload.name;
    }
    if (pluginParams.length > 0) {
      payload.parameters = serializeParams(paramValues);
    }
    // Include Cognito refresh token if schedule has GDrive picker params
    // (needed for server-side token resolution during scheduled execution)
    const serializedParams = payload.parameters as Record<string, unknown> | undefined;
    if (serializedParams) {
      const hasGDriveParams = Object.values(serializedParams).some((v) => {
        if (typeof v === "object" && v !== null) {
          const entry = v as Record<string, unknown>;
          if (entry.appCredentialId) return true;
          const inner = entry.value;
          if (typeof inner === "object" && inner !== null && (inner as Record<string, unknown>).appCredentialId) return true;
        }
        return false;
      });
      if (hasGDriveParams) {
        const um = getUserManager();
        const user = await um?.getUser();
        if (user?.refresh_token) {
          payload.cognitoRefreshToken = user.refresh_token;
        } else {
          setSaveError(
            "Unable to configure Google Drive authentication for this schedule. " +
            "Please sign out and sign in again, then retry."
          );
          return;
        }
      }
    }
    createSchedule.mutate(payload as Partial<Schedule>, {
      onSuccess: () => { closeDialog(); },
      onError: (err) => { setSaveError(err.message || "Save failed, please try again"); },
    });
  };

  const handleUpdate = () => {
    if (!isEditing) return;
    const schedule = dialogMode as Schedule;
    const payload: Record<string, unknown> = {
      scheduleId: schedule.scheduleId,
      cronExpression: form.cronExpression,
      timezone: form.timezone,
      name: form.name,
    };
    // Full replacement semantics (Req 3.6): PUT sends entire parameters dict
    if (pluginParams.length > 0) {
      payload.parameters = serializeParams(paramValues);
    }
    updateSchedule.mutate(payload as { scheduleId: string } & Record<string, unknown>, {
      onSuccess: () => { closeDialog(); },
      onError: (err) => { setSaveError(err.message || "Save failed, please try again"); },
    });
  };

  const toggleEnabled = (id: string, current: boolean) => {
    updateSchedule.mutate({ scheduleId: id, enabled: !current });
  };

  const handleReauth = async (schedule: Schedule) => {
    const um = getUserManager();
    const user = await um?.getUser();
    if (!user?.refresh_token) {
      setSaveError("Unable to refresh authentication. Please sign out and sign in again.");
      return;
    }
    updateSchedule.mutate(
      {
        scheduleId: schedule.scheduleId,
        cognitoRefreshToken: user.refresh_token,
        authStatus: "active",
      },
      {
        onError: (err) => setSaveError(err.message || "Failed to refresh authentication"),
      },
    );
  };

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;

  const schedules = data?.schedules ?? [];
  const plugins = pluginsData?.plugins ?? [];

  const canSubmit = form.pluginId && form.cronExpression && !hasParamErrors;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4">Schedules</Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={openCreateDialog}>New Schedule</Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Plugin</TableCell>
              <TableCell>Cron</TableCell>
              <TableCell>Timezone</TableCell>
              <TableCell>Next Run</TableCell>
              <TableCell>Last Run</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Failures</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schedules.map((s) => (
              <TableRow key={s.scheduleId} hover>
                <TableCell>{s.name || s.pluginName || s.pluginId}</TableCell>
                <TableCell>{s.pluginName ?? s.pluginId}</TableCell>
                <TableCell><code>{s.cronExpression}</code></TableCell>
                <TableCell>{s.timezone}</TableCell>
                <TableCell>{s.nextRunAt ? new Date(s.nextRunAt).toLocaleString() : "—"}</TableCell>
                <TableCell>{s.lastRunAt ? new Date(s.lastRunAt).toLocaleString() : "—"}</TableCell>
                <TableCell>
                  <Chip
                    label={
                      s.authStatus === "auth_expired" ? "Auth Expired" :
                      s.enabled ? "Enabled" :
                      s.consecutiveFailures >= 3 ? "Auto-paused" : "Paused"
                    }
                    color={
                      s.authStatus === "auth_expired" ? "warning" :
                      s.enabled ? "success" :
                      s.consecutiveFailures >= 3 ? "error" : "default"
                    }
                    size="small"
                    onClick={s.authStatus === "auth_expired" ? () => handleReauth(s) : undefined}
                    sx={s.authStatus === "auth_expired" ? { cursor: "pointer" } : undefined}
                  />
                </TableCell>
                <TableCell>{s.consecutiveFailures || 0}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEditDialog(s)}><EditIcon fontSize="small" /></IconButton>
                  <Switch size="small" checked={s.enabled} onChange={() => toggleEnabled(s.scheduleId, s.enabled)} />
                  <IconButton size="small" onClick={() => deleteSchedule.mutate(s.scheduleId)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {schedules.length === 0 && (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>No schedules yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? "Edit Schedule" : "New Schedule"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          {/* Schedule Name — optional, displayed in table and used for identification */}
          <TextField
            label="Schedule Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            size="small"
            fullWidth
            helperText="Optional name to identify this schedule"
            inputProps={{ maxLength: 200 }}
          />

          {/* Plugin selector — disabled in edit mode (can't change plugin of existing schedule) */}
          <TextField
            select
            label="Plugin"
            value={form.pluginId}
            onChange={(e) => setForm((f) => ({ ...f, pluginId: e.target.value }))}
            size="small"
            fullWidth
            SelectProps={{ native: true }}
            disabled={isEditing}
          >
            <option value="">Select plugin…</option>
            {plugins.map((p) => <option key={p.pluginId} value={p.pluginId}>{p.name}</option>)}
          </TextField>

          <TextField
            label="Cron Expression"
            value={form.cronExpression}
            onChange={(e) => setForm((f) => ({ ...f, cronExpression: e.target.value }))}
            size="small"
            helperText="5-field cron: min hour dom month dow"
            fullWidth
          />
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {PRESETS.map((p) => (
              <Chip key={p.value} label={p.label} size="small" variant="outlined" onClick={() => setForm((f) => ({ ...f, cronExpression: p.value }))} />
            ))}
          </Box>
          <TextField
            label="Timezone"
            value={form.timezone}
            onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
            size="small"
            fullWidth
          />

          {/* Parameter form — shown when plugin has parameters */}
          {form.pluginId && paramsLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {pluginParams.length > 0 && !paramsLoading && (
            <ScheduleParameterForm
              parameters={pluginParams}
              values={paramValues}
              onChange={setParamValues}
              errors={paramErrors}
              pluginId={activePluginId ?? undefined}
            />
          )}

          {/* Save error message — retained on failure (Req 5.7) */}
          {saveError && (
            <Alert severity="error" onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={isEditing ? handleUpdate : handleCreate}
            disabled={!canSubmit}
          >
            {isEditing ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
